var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { DropShiftHeaderMessageType } from './DropShiftModalContentTypes';
import { ShiftFlowModalContentTypes } from '../ShiftFlowModalTypes';
import { gql } from '@apollo/client';
import { useDropShiftMutation } from '@amzn/atoz-shiftapp-types';
import { useLocation, useListOfSkills } from '../../../hooks';
import { shiftHasDropPenalty } from '../../../utils';
import { BIAnalytics, useAssociateContext } from '@amzn/atoz-shiftapp-utilities';
import { getOpportunitiesRefetchQuery } from '../../../utils/refetchQueries';
import { getRouteFromPath, PageName } from '@amzn/atoz-shiftapp-initializer';
import { DateTime } from 'luxon';
import { useDropShiftModalTitle } from '../hooks/useDropShiftModalTitle';
export var getHeaderMessageType = function (isDroppable, hasDropPenalty) {
    // User should never be able to see the undroppable message, as they should not be able to
    // open this modal if the shift is undroppable. But will include here just in case
    if (!isDroppable) {
        return DropShiftHeaderMessageType.undroppable;
    }
    if (hasDropPenalty) {
        return DropShiftHeaderMessageType.dropPenalty;
    }
    return DropShiftHeaderMessageType.countdown;
};
export var useDropShiftModalContentData = function (_a) {
    var dropOpportunity = _a.dropOpportunity, setModalContent = _a.setModalContent, setModalError = _a.setModalError, scheduleShift = _a.scheduleShift;
    var pathname = useLocation().pathname;
    var features = useAssociateContext().features;
    var skillsString = useListOfSkills(scheduleShift.skills);
    var dropDeadlineFromDropOpportunity = (dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.dropDeadline)
        ? DateTime.fromISO(dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.dropDeadline)
        : null;
    var dropWithoutPenaltyDeadline = !!(dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.penaltyStartTime)
        ? DateTime.fromISO(dropOpportunity.penaltyStartTime)
        : null;
    var dropDeadline = dropWithoutPenaltyDeadline || dropDeadlineFromDropOpportunity;
    var hasDropPenalty = shiftHasDropPenalty(scheduleShift, dropOpportunity);
    var headerMessageType = getHeaderMessageType(!!dropOpportunity, hasDropPenalty);
    var titleText = useDropShiftModalTitle(headerMessageType);
    var _b = useDropShiftMutation({
        variables: {
            shiftOpportunityId: {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
                shiftOpportunityId: dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.id
            }
        },
        update: function (cache) {
            var scheduleIdentifier = cache.identify({
                __typename: 'Schedule'
            });
            cache.modify({
                id: scheduleIdentifier,
                fields: {
                    scheduleShifts: function (scheduleShifts, _a) {
                        var readField = _a.readField;
                        return scheduleShifts.filter(function (scheduleShiftRef) {
                            var shift = readField('shift', scheduleShiftRef);
                            var shiftId = readField('id', shift);
                            return shiftId !== scheduleShift.shift.id;
                        });
                    }
                }
            });
            cache.gc();
        },
        refetchQueries: getOpportunitiesRefetchQuery(pathname, features.findShiftsAPICallRedesign),
        awaitRefetchQueries: true,
        onCompleted: function () {
            setModalContent(ShiftFlowModalContentTypes.dropSuccess);
        },
        onError: function (error) {
            BIAnalytics.dropShiftFailure(scheduleShift.shift.id, error.message);
            setModalError(error);
            setModalContent(ShiftFlowModalContentTypes.dropError);
        }
    }), onDropShift = _b[0], loading = _b[1].loading;
    var onClickDropNow = function () {
        if (!hasDropPenalty) {
            if (getRouteFromPath(pathname) === PageName.FindShifts) {
                BIAnalytics.noPenaltyDropShiftFromFindShiftPage(scheduleShift.shift.id, scheduleShift.shift.timeRange.start, scheduleShift.shift.timeRange.end, skillsString, (dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.id) || undefined);
            }
            BIAnalytics.noPenaltyShiftDropConfirmation(scheduleShift.shift.id, scheduleShift.shift.timeRange.start, scheduleShift.shift.timeRange.end, skillsString, (dropOpportunity === null || dropOpportunity === void 0 ? void 0 : dropOpportunity.id) || undefined);
        }
        BIAnalytics.dropShift(scheduleShift.shift.id);
        onDropShift();
    };
    return {
        onClickDropNow: onClickDropNow,
        dropNowIsSubmitting: loading,
        hasDropPenalty: hasDropPenalty,
        titleText: titleText,
        headerMessageType: headerMessageType,
        dropDeadline: dropDeadline
    };
};
export var DROP_SHIFT_MUTATION = gql(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    mutation DropShift($shiftOpportunityId: DropShiftInput!) {\n        dropShift(input: $shiftOpportunityId)\n    }\n"], ["\n    mutation DropShift($shiftOpportunityId: DropShiftInput!) {\n        dropShift(input: $shiftOpportunityId)\n    }\n"])));
useDropShiftModalContentData.fragments = {
    scheduleShift: gql(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n        fragment UseDropShiftModalContentData_ScheduleShift on ScheduleShift {\n            skill\n            skills {\n                translationKey\n                namespace\n                defaultText\n            }\n            shift {\n                id\n                timeRange {\n                    start\n                    end\n                }\n            }\n        }\n    "], ["\n        fragment UseDropShiftModalContentData_ScheduleShift on ScheduleShift {\n            skill\n            skills {\n                translationKey\n                namespace\n                defaultText\n            }\n            shift {\n                id\n                timeRange {\n                    start\n                    end\n                }\n            }\n        }\n    "]))),
    shiftOpportunity: gql(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n        fragment UseDropShiftModalContentData_ShiftOpportunity on ShiftOpportunity {\n            id\n            type\n            penaltyStartTime\n            dropDeadline\n            shift {\n                id\n            }\n        }\n    "], ["\n        fragment UseDropShiftModalContentData_ShiftOpportunity on ShiftOpportunity {\n            id\n            type\n            penaltyStartTime\n            dropDeadline\n            shift {\n                id\n            }\n        }\n    "])))
};
var templateObject_1, templateObject_2, templateObject_3;
